/**
 * Tenant switcher — lets admins jump between portfolios (their own
 * pinnacle + any client tenants). Hidden entirely for viewers, and for
 * admins who only have access to one tenant.
 *
 * Switching calls POST /api/tenants/switch which re-signs the session
 * cookie with the new tid, then reloads so every page re-fetches
 * scoped data.
 */

import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Building2, ChevronDown, Check } from 'lucide-react'
import { useAuth } from '@/data/auth'
import { listTenants, switchTenant } from '@/data/auth'
import { cn } from '@/lib/utils'

export default function TenantSwitcher({ compact = false }) {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [tenants, setTenants] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [switching, setSwitching] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    listTenants()
      .then(({ tenants, active_tenant_id }) => {
        if (cancelled) return
        setTenants(tenants)
        setActiveId(active_tenant_id)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [user])

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  if (!user) return null
  // Only show for users with access to more than one tenant. Viewers with
  // one tenant, admins with only their own — no switcher clutter.
  if (!tenants || tenants.length < 2) return null

  const activeTenant = tenants.find((t) => t.id === activeId) || tenants[0]

  const onSwitch = async (tenantId) => {
    if (tenantId === activeId) { setOpen(false); return }
    setSwitching(true)
    try {
      await switchTenant(tenantId)
      // Full reload so every page's data fetches pick up the new tenant.
      window.location.reload()
    } catch (e) {
      setSwitching(false)
      alert(`Couldn't switch: ${e.message}`)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        disabled={switching}
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition disabled:opacity-50',
          compact ? 'px-2 py-1' : 'px-3 py-1.5',
        )}
        title={`Active portfolio: ${activeTenant.display_name}`}
      >
        <Building2 className={cn('text-white/50', compact ? 'w-3.5 h-3.5' : 'w-3.5 h-3.5')} />
        {!compact && (
          <span className="font-mono text-[10px] text-white/75 max-w-[140px] truncate">
            {activeTenant.display_name}
          </span>
        )}
        <ChevronDown className="w-3 h-3 text-white/40" />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="absolute right-0 mt-2 w-72 rounded-xl border border-white/[0.08] bg-navy-800/95 backdrop-blur-xl shadow-2xl p-1 z-40"
        >
          <div className="px-3 py-2 border-b border-white/[0.05] mb-1">
            <div className="font-mono text-[9px] tracking-[0.14em] uppercase text-white/40">
              Portfolios ({tenants.length})
            </div>
          </div>
          {tenants.map((t) => (
            <button
              key={t.id}
              onClick={() => onSwitch(t.id)}
              className={cn(
                'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-left transition',
                t.id === activeId
                  ? 'bg-white/[0.06] text-white/95'
                  : 'text-white/70 hover:text-white/95 hover:bg-white/[0.04]',
              )}
            >
              <div className="min-w-0">
                <div className="text-[13px] leading-tight truncate">{t.display_name}</div>
                <div className="font-mono text-[10px] text-white/40 truncate">{t.slug}</div>
              </div>
              {t.id === activeId && <Check className="w-3.5 h-3.5 text-[#a0d4ff] flex-shrink-0" />}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  )
}
